import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, CheckCircle2, Star, ArrowLeft, Flower, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const testimonials = [{
  name: 'Sarah Mitchell',
  role: 'Marketing Executive',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1887&auto=format&fit=crop',
  text: 'This course completely transformed my relationship with stress. I found a peace I never knew existed within me.',
  rating: 5
}, {
  name: 'David Chen',
  role: 'Software Engineer',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1887&auto=format&fit=crop',
  text: 'The techniques I learned here have become essential to my daily routine. Life-changing experience.',
  rating: 5
}, {
  name: 'Priya Sharma',
  role: 'Yoga Instructor',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2070&auto=format&fit=crop',
  text: 'Even as a yoga teacher, this course deepened my practice in ways I never imagined. Highly recommended.',
  rating: 5
}];
const faqs = [{
  question: 'Do I need prior meditation experience?',
  answer: 'No prior experience is necessary. This course is designed for both beginners and those looking to deepen their existing practice.'
}, {
  question: 'What should I bring?',
  answer: 'Comfortable clothing, a journal, water bottle, and an open mind. Meditation cushions and mats are provided.'
}, {
  question: 'Is accommodation included?',
  answer: 'Yes, the course fee includes shared accommodation, all meals (vegetarian), and course materials.'
}, {
  question: 'Can I attend if I have physical limitations?',
  answer: 'Absolutely. All practices can be modified for different physical abilities. Please inform us of any concerns during registration.'
}, {
  question: 'What is your cancellation policy?',
  answer: 'Full refund if cancelled 30+ days before start date. 50% refund for 15-29 days. No refund within 14 days of course start.'
}];
export function CourseDetailPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-426">
      <Header data-id="element-427" />

      {/* Hero Image Section */}
      <section className="relative h-[70vh] min-h-[600px] w-full overflow-hidden" data-id="element-428">
        <motion.div initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="absolute inset-0" data-id="element-429">
          <img src="https://images.unsplash.com/photo-1528319725582-ddc096101511?q=80&w=2070&auto=format&fit=crop" alt="Meditation sanctuary at sunrise" className="w-full h-full object-cover" data-id="element-430" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FFFBF5]" data-id="element-431" />
        </motion.div>

        {/* Breadcrumb */}
        <div className="absolute top-24 left-0 right-0 z-10" data-id="element-432">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-433">
            <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-434">
              <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors backdrop-blur-sm bg-black/20 px-4 py-2 rounded-full" data-id="element-435">
                <ArrowLeft className="w-4 h-4" data-id="element-436" />
                <span data-id="element-437">Back to Courses</span>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 pb-16" data-id="element-438">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-439">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="max-w-4xl" data-id="element-440">
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-441">
                <Flower className="w-4 h-4" data-id="element-442" />
                <span data-id="element-443">Enrolling Now</span>
              </div>

              <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-444">
                Awakening the Inner Light
              </h1>

              <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow-lg" data-id="element-445">
                A transformative 5-day journey into the depths of your
                consciousness
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Course Introduction & Details */}
      <section className="py-20 bg-white relative -mt-1" data-id="element-446">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-447">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-448">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-449">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} data-id="element-450">
                {/* Key Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 pb-12 border-b border-gray-100" data-id="element-451">
                  <div className="flex items-center gap-3 text-[#5D6D7E]" data-id="element-452">
                    <Calendar className="w-6 h-6 text-[#8B9D83]" data-id="element-453" />
                    <div data-id="element-454">
                      <div className="text-xs text-[#5D6D7E]/70 uppercase tracking-wide" data-id="element-455">
                        Dates
                      </div>
                      <div className="font-semibold text-[#2C3E50] text-lg" data-id="element-456">
                        Oct 15-20
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[#5D6D7E]" data-id="element-457">
                    <Clock className="w-6 h-6 text-[#8B9D83]" data-id="element-458" />
                    <div data-id="element-459">
                      <div className="text-xs text-[#5D6D7E]/70 uppercase tracking-wide" data-id="element-460">
                        Duration
                      </div>
                      <div className="font-semibold text-[#2C3E50] text-lg" data-id="element-461">
                        5 Days
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[#5D6D7E]" data-id="element-462">
                    <Users className="w-6 h-6 text-[#8B9D83]" data-id="element-463" />
                    <div data-id="element-464">
                      <div className="text-xs text-[#5D6D7E]/70 uppercase tracking-wide" data-id="element-465">
                        Group Size
                      </div>
                      <div className="font-semibold text-[#2C3E50] text-lg" data-id="element-466">
                        Max 20
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[#5D6D7E]" data-id="element-467">
                    <MapPin className="w-6 h-6 text-[#8B9D83]" data-id="element-468" />
                    <div data-id="element-469">
                      <div className="text-xs text-[#5D6D7E]/70 uppercase tracking-wide" data-id="element-470">
                        Location
                      </div>
                      <div className="font-semibold text-[#2C3E50] text-lg" data-id="element-471">
                        On-site
                      </div>
                    </div>
                  </div>
                </div>

                {/* Introduction */}
                <div className="prose prose-lg max-w-none mb-12" data-id="element-472">
                  <h2 className="font-serif text-3xl text-[#2C3E50] mb-6" data-id="element-473">
                    About This Journey
                  </h2>

                  <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-474">
                    In today's fast-paced world, we often lose connection with
                    our inner essence—the radiant light that exists within each
                    of us. This intensive 5-day retreat is designed to help you
                    rediscover and awaken this inner luminosity through ancient
                    meditation practices, breathwork, and guided self-inquiry.
                  </p>

                  <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-475">
                    Drawing from the wisdom of Vedic traditions and adapted for
                    modern practitioners, this course offers a comprehensive
                    approach to spiritual awakening. You'll learn powerful
                    techniques that have been practiced for thousands of years,
                    now made accessible and relevant for contemporary life.
                  </p>

                  <p className="text-lg text-[#5D6D7E] leading-relaxed mb-8" data-id="element-476">
                    Under the guidance of Satguru Dev, you'll explore the depths
                    of consciousness, release limiting patterns, and cultivate a
                    direct experience of your true nature. This isn't just about
                    learning meditation—it's about fundamentally transforming
                    your relationship with yourself and the world around you.
                  </p>
                </div>

                {/* Who Should Attend */}
                <div className="bg-[#FAF7F2] rounded-2xl p-8 mb-12 border-l-4 border-[#D4AF37]" data-id="element-477">
                  <h3 className="font-serif text-2xl text-[#2C3E50] mb-6" data-id="element-478">
                    Who Should Attend
                  </h3>
                  <ul className="space-y-4 text-[#5D6D7E]" data-id="element-479">
                    <li className="flex items-start gap-3" data-id="element-480">
                      <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-1" data-id="element-481" />
                      <span data-id="element-482">
                        Beginners seeking a structured introduction to
                        meditation and spiritual practice
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-483">
                      <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-1" data-id="element-484" />
                      <span data-id="element-485">
                        Experienced practitioners looking to deepen their
                        understanding and practice
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-486">
                      <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-1" data-id="element-487" />
                      <span data-id="element-488">
                        Anyone experiencing stress, anxiety, or a sense of
                        disconnection from their true self
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-489">
                      <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-1" data-id="element-490" />
                      <span data-id="element-491">
                        Individuals seeking clarity, purpose, and a deeper
                        connection to life
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-492">
                      <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-1" data-id="element-493" />
                      <span data-id="element-494">
                        Those ready to commit to personal transformation and
                        spiritual growth
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Video Preview */}
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl group cursor-pointer" data-id="element-495">
                  <img src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=2070&auto=format&fit=crop" alt="Course preview" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-496" />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center" data-id="element-497">
                    <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl" data-id="element-498">
                      <Play className="w-7 h-7 text-[#8B9D83] fill-current ml-1" data-id="element-499" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar - Enrollment Card */}
            <div className="lg:col-span-1" data-id="element-500">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="sticky top-24 bg-white rounded-2xl shadow-xl p-8 border border-gray-100" data-id="element-501">
                <div className="text-center mb-6" data-id="element-502">
                  <div className="text-4xl font-serif font-bold text-[#2C3E50] mb-2" data-id="element-503">
                    $899
                  </div>
                  <div className="text-sm text-[#5D6D7E]" data-id="element-504">
                    All-inclusive package
                  </div>
                </div>

                <button className="w-full bg-[#8B9D83] hover:bg-[#7A8C72] text-white py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg mb-4" data-id="element-505">
                  Enroll Now
                </button>

                <button className="w-full border-2 border-[#8B9D83] text-[#8B9D83] hover:bg-[#8B9D83] hover:text-white py-3 rounded-full font-semibold transition-all duration-300" data-id="element-506">
                  Download Brochure
                </button>

                <div className="mt-8 pt-8 border-t border-gray-100 space-y-4" data-id="element-507">
                  <div className="flex items-start gap-3" data-id="element-508">
                    <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-0.5" data-id="element-509" />
                    <span className="text-sm text-[#5D6D7E]" data-id="element-510">
                      5 days of intensive training
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-511">
                    <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-0.5" data-id="element-512" />
                    <span className="text-sm text-[#5D6D7E]" data-id="element-513">
                      Accommodation & meals included
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-514">
                    <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-0.5" data-id="element-515" />
                    <span className="text-sm text-[#5D6D7E]" data-id="element-516">
                      Personal guidance sessions
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-517">
                    <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-0.5" data-id="element-518" />
                    <span className="text-sm text-[#5D6D7E]" data-id="element-519">
                      Course materials & certificate
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-520">
                    <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-0.5" data-id="element-521" />
                    <span className="text-sm text-[#5D6D7E]" data-id="element-522">
                      Lifetime community access
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 text-center text-xs text-[#5D6D7E]" data-id="element-523">
                  <p data-id="element-524">Only 6 spots remaining</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-525">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-526">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-527">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-528">
              What You'll Learn
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12 max-w-2xl mx-auto" data-id="element-529">
              Comprehensive practices and teachings to support your spiritual
              awakening
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-530">
              {[{
              title: 'Ancient Breathing Techniques',
              description: 'Master pranayama practices that calm the mind, energize the body, and prepare you for deep meditation states'
            }, {
              title: 'Chakra Activation',
              description: 'Learn to awaken and balance your seven energy centers, releasing blockages and enhancing vitality'
            }, {
              title: 'Deep Meditation States',
              description: 'Experience profound states of consciousness, inner peace, and connection to your true essence'
            }, {
              title: 'Energy Healing',
              description: 'Discover techniques to heal yourself and support others through conscious energy work and intention'
            }, {
              title: 'Mindful Living',
              description: 'Integrate meditation into your daily life for lasting transformation, clarity, and presence'
            }, {
              title: 'Spiritual Community',
              description: 'Connect with like-minded seekers and build lasting relationships on the path to enlightenment'
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl shadow-md" data-id="element-531">
                  <div className="w-12 h-12 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mb-4" data-id="element-532">
                    <CheckCircle2 className="w-6 h-6 text-[#8B9D83]" data-id="element-533" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-2" data-id="element-534">
                    {item.title}
                  </h3>
                  <p className="text-[#5D6D7E]" data-id="element-535">{item.description}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Daily Experience */}
      <section className="py-20 bg-white" data-id="element-536">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-537">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-538">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-8 text-center" data-id="element-539">
              Your Daily Experience
            </h2>

            <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 shadow-lg" data-id="element-540">
              <div className="space-y-8" data-id="element-541">
                <div data-id="element-542">
                  <h3 className="font-serif text-2xl text-[#2C3E50] mb-4" data-id="element-543">
                    Morning Practice (6:00 AM - 9:00 AM)
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-544">
                    Begin each day with sunrise meditation, pranayama
                    breathwork, and gentle yoga. Experience the profound
                    stillness of early morning as you set intentions for the day
                    ahead.
                  </p>
                </div>

                <div data-id="element-545">
                  <h3 className="font-serif text-2xl text-[#2C3E50] mb-4" data-id="element-546">
                    Teaching Sessions (10:00 AM - 12:30 PM)
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-547">
                    Engage in interactive teachings with Satguru Dev, exploring
                    meditation philosophy, energy systems, and practical
                    techniques for spiritual awakening. Ask questions and
                    receive personalized guidance.
                  </p>
                </div>

                <div data-id="element-548">
                  <h3 className="font-serif text-2xl text-[#2C3E50] mb-4" data-id="element-549">
                    Afternoon Integration (2:00 PM - 5:00 PM)
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-550">
                    Practice what you've learned through guided meditation
                    sessions, walking meditation in nature, journaling, and
                    optional one-on-one consultations with instructors.
                  </p>
                </div>

                <div data-id="element-551">
                  <h3 className="font-serif text-2xl text-[#2C3E50] mb-4" data-id="element-552">
                    Evening Reflection (6:00 PM - 8:00 PM)
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-553">
                    Gather for evening discourse, group meditation, and
                    community sharing. Connect with fellow participants and
                    integrate the day's experiences before silent contemplation.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-554">
                <p className="text-sm text-[#5D6D7E] italic" data-id="element-555">
                  All meals are vegetarian and prepared with mindfulness. Free
                  time is built into the schedule for rest, personal practice,
                  and integration.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Instructor */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-556">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-557">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-558">
            <div className="relative" data-id="element-559">
              <img src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?q=80&w=2070&auto=format&fit=crop" alt="Satguru Dev" className="rounded-2xl shadow-2xl w-full" data-id="element-560" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#D4AF37]/20 rounded-full blur-3xl -z-10" data-id="element-561" />
            </div>

            <div data-id="element-562">
              <span className="text-[#8B9D83] font-medium tracking-widest uppercase text-sm mb-2 block" data-id="element-563">
                Your Guide
              </span>
              <h2 className="font-serif text-4xl text-[#2C3E50] mb-6" data-id="element-564">
                Satguru Dev
              </h2>
              <p className="text-[#5D6D7E] mb-4 leading-relaxed" data-id="element-565">
                With over 40 years of dedicated practice and teaching, Satguru
                Dev has guided thousands of students on their spiritual journey.
                Trained in the ancient Himalayan traditions, he brings authentic
                wisdom combined with a deep understanding of modern life's
                challenges.
              </p>
              <p className="text-[#5D6D7E] mb-6 leading-relaxed" data-id="element-566">
                His compassionate teaching style and profound insights create a
                safe, transformative space for students to explore their inner
                landscape and awaken their true potential.
              </p>
              <div className="flex items-center gap-8 text-sm" data-id="element-567">
                <div data-id="element-568">
                  <div className="font-serif text-3xl font-bold text-[#8B9D83]" data-id="element-569">
                    40+
                  </div>
                  <div className="text-[#5D6D7E]" data-id="element-570">Years Teaching</div>
                </div>
                <div data-id="element-571">
                  <div className="font-serif text-3xl font-bold text-[#8B9D83]" data-id="element-572">
                    10K+
                  </div>
                  <div className="text-[#5D6D7E]" data-id="element-573">Students</div>
                </div>
                <div data-id="element-574">
                  <div className="font-serif text-3xl font-bold text-[#8B9D83]" data-id="element-575">
                    500+
                  </div>
                  <div className="text-[#5D6D7E]" data-id="element-576">Courses</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-id="element-577">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-578">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-579">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-580">
              Student Experiences
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12" data-id="element-581">
              Hear from those who have walked this path
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-582">
              {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#FAF7F2] p-8 rounded-2xl shadow-md" data-id="element-583">
                  <div className="flex gap-1 mb-4" data-id="element-584">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" data-id="element-585" />)}
                  </div>
                  <p className="text-[#5D6D7E] mb-6 italic leading-relaxed" data-id="element-586">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4" data-id="element-587">
                    <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" data-id="element-588" />
                    <div data-id="element-589">
                      <div className="font-semibold text-[#2C3E50]" data-id="element-590">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-[#5D6D7E]" data-id="element-591">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-592">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-593">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-594">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-595">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6" data-id="element-596">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl shadow-md" data-id="element-597">
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3" data-id="element-598">
                    {faq.question}
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-599">{faq.answer}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#8B9D83] to-[#7A8C72] text-white" data-id="element-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-601">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-602">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-603">
              Begin Your Transformation Today
            </h2>
            <p className="text-xl mb-10 text-white/90" data-id="element-604">
              Join us for this life-changing journey and discover the light that
              has always been within you
            </p>
            <button className="bg-white text-[#8B9D83] hover:bg-[#FAF7F2] px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl" data-id="element-605">
              Enroll in Awakening the Inner Light
            </button>
            <p className="mt-6 text-sm text-white/70" data-id="element-606">
              Limited to 20 participants • Enrollment closes October 10th
            </p>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-607" />
    </div>;
}